// app.ai._index.tsx — the AI Assistant home: the AI Brief form, monthly AI
// budget, and the recent AI activity feed.
export const ai = {
  page: {
    heading: "AI Assistant",
    intro:
      "This is your AI's home. Quote Desk's AI prices quote lines, drafts counter-offers, and reads purchase orders — always as a draft you approve, never on its own. Teach it your policy and voice once below, and every suggestion follows it.",
  },
  banners: {
    briefSaved:
      "AI Brief saved — every new suggestion, counter-offer, and buyer-facing draft now follows it.",
    budgetSaved: "Monthly AI budget set to ${{amount}}.",
  },
  budget: {
    heading: "This month's AI usage",
    usedOf: "used of",
    remainingBadge: "{{amount}} remaining",
    budgetReachedBadge: "Budget reached — AI paused until the 1st",
    description:
      "Covers AI price suggestions, counter-offers, and PO parsing. When the cap is reached, AI features pause with a clear message (nothing breaks) and resume automatically on the 1st.",
    monthlyBudgetLabel: "Monthly budget (USD)",
    monthlyBudgetHelpText: "Between ${{min}} and ${{max}}. Typical heavy month: $10–15.",
    saveButton: "Save budget",
  },
  brief: {
    heading: "AI Brief — teach your AI once",
    description:
      "The Brief rides along with every AI request, so suggestions sound like you and stay inside your pricing rules. Sensible defaults apply until you change them.",
    businessContextLabel: "Business context",
    businessContextPlaceholder:
      "e.g. We wholesale specialty coffee equipment to cafés and roasters across North America.",
    businessContextHelpText:
      "What you sell and who buys it — helps the AI reason like someone on your team.",
    maxDiscountLabel: "Maximum discount (%)",
    maxDiscountHelpText: "Hard ceiling off list price. Enforced in code, not just told to the AI.",
    floorMarginLabel: "Floor margin (%) — optional",
    floorMarginHelpText:
      "Never price below this gross margin when unit cost is known. Leave blank to skip.",
    preferredTermsLabel: "Preferred payment terms",
    preferredTermsPlaceholder: "e.g. Net 30",
    neverOfferLabel: "Never offer",
    neverOfferPlaceholder:
      "e.g. Free shipping on orders under $2,000. Discounts on the Pro-X line.",
    neverOfferHelpText: "Hard rules the AI must never propose, in plain language.",
    toneLabel: "Tone",
    negotiationStanceLabel: "Negotiation stance",
    buyerKnowledgeLabel: "Buyer knowledge level",
    buyerKnowledgeAutoOption: "auto (match the buyer)",
    voiceSampleLabel: "Brand voice sample — optional",
    voiceSampleHelpText:
      "Paste a paragraph that sounds like you — the AI matches it in buyer-facing wording.",
    signatureNameLabel: "Sign buyer-facing messages as",
    signatureNamePlaceholder: "e.g. Dana at Summit Supply",
    currencyLabel: "Currency",
    currencyHelpText: "3-letter code the AI uses when talking prices — usually your store currency.",
    saveButton: "Save AI Brief",
  },
  activity: {
    heading: "Recent AI activity",
    empty:
      "Nothing yet — AI activity lands here as you work. Open a draft quote and try “Suggest prices with AI”, or upload a PO to see the parser in action. Every run shows its cost and how much order history backed it.",
    kindPriceSuggestion: "Price suggestion",
    kindCounterOffer: "Counter-offer",
    dataPrefix: "data:",
    status: {
      DRAFT: "Draft",
      APPLIED: "Applied",
      DISMISSED: "Dismissed",
      REFUSED: "Refused",
    },
  },
  errors: {
    saveBriefFailed: "Couldn't save the AI Brief. Please try again.",
  },
  // Validation messages + interpolated field labels surfaced by the shared
  // pure-function form parser (app/lib/aiBriefForm.ts). Labels are interpolated
  // into the messages via {{label}}, so they must read naturally mid-sentence.
  briefForm: {
    labelFloorMargin: "Floor margin",
    labelMaxDiscount: "Maximum discount",
    labelBusinessContext: "Business context",
    labelPreferredTerms: "Preferred payment terms",
    labelNeverOffer: "Never offer",
    labelSignatureName: "Signature name",
    labelVoiceSample: "Voice sample",
    mustBeNumber: "{{label}} must be a number (e.g. 15 for 15%).",
    mustBeBetween: "{{label}} must be between {{min}}% and {{max}}%.",
    maxDiscountRequired: "Maximum discount is required (enter 0 to forbid discounts).",
    pickTone: "Pick a tone.",
    pickBuyerKnowledge: "Pick a buyer knowledge level.",
    pickStance: "Pick a negotiation stance.",
    currencyInvalid: "Currency must be a 3-letter code (e.g. USD).",
    textTooLong: "{{label}} is too long (max {{max}} characters).",
    budgetPositive: "Monthly AI budget must be a positive dollar amount.",
  },
};
